/** Sign in: password, then a second factor when the role or workspace requires one.
 *
 * Three things about this screen are the point of it.
 *
 * **One message for every refusal.** The API returns the same 401 whether the address is unknown, the
 * password wrong, the account suspended or the credential locked — so this form shows one message. A
 * helpful "no account with that address" would undo the server's care and turn the form into an
 * address validator.
 *
 * **MFA is a step, not a setting.** An Admin or Approver who has just signed in with a correct
 * password is *not* signed in; they are holding a five-minute half-session. The screen says so rather
 * than appearing to succeed and then failing on the next request.
 *
 * **Recovery codes are shown once and never stored.** They come back from enrolment, are rendered,
 * and are gone on the next navigation — `useEnrolMfa` uses a mutation with `gcTime: 0` so there is no
 * cache for them to linger in. The copy says "once", because it is true.
 */

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useCompleteMfa, useEnrolMfa, useLogin, useMe } from "../api/auth";
import { usePasskeySignIn } from "../api/passkeys";
import { isSignedOut } from "../api/refusal";
import {
  SESSION_ENDED_NOTICE,
  SESSION_ENDED_TYPING_NOTICE,
  clearSessionEnded,
  sessionEndedLostTyping,
  sessionEndedPending,
} from "../api/sessionEnded";
import { clearSwitchPending, switchPendingRead } from "../api/switchPending";
import { passkeysSupported } from "../api/webauthn";
import { AuthCard, AuthField, AuthNotice, AuthPage } from "../components/AuthLayout";
import { QrCode } from "../components/QrCode";
import { useDocumentTitle } from "../shell/documentTitle";
import { entryFor, sectionById } from "../shell/routeTable";
import styles from "../components/AuthLayout.module.css";

/** The one message every refusal produces. */
const REFUSED = "Those details are not valid. Check them and try again.";

/** Gate 12. Said apart from `REFUSED` because the remedy is the opposite one: stop, rather than
 * check and retry. The API answers 429 when an address has asked to sign in too often in a minute
 * (ADR-0073). It names no account and no count: a sentence that varied by address would tell a
 * guesser which ones exist. */
const THROTTLED = "Too many sign-in attempts. Wait a minute before trying again.";

/** The one message a refused passkey produces. Like `REFUSED`, it does not say *why* - the API
 * answers every passkey refusal with the same 401, and a form that explained would undo it. It
 * always names the way out, because a passkey that does not work must never be a dead end
 * (ADR-0105: password and authenticator code remain). */
const PASSKEY_REFUSED =
  "That passkey was not accepted. Try again, or sign in with your email and password.";
const PASSKEY_CANCELLED =
  "The passkey prompt was closed. Try again, or sign in with your email and password.";
const PASSKEY_UNAVAILABLE =
  "Passkeys are not available right now. Sign in with your email and password.";

type Stage = "credentials" | "enrol" | "code";

/** The id the refusal notice carries, so the fields it is about can point at it. */
const NOTICE_ID = "sign-in-notice";

/**
 * @param inPlace - the form is standing in for a screen at its own address (`RequireSession`), as
 *   opposed to being the `/sign-in` route. Only an in-place form can return somebody to where they
 *   were by doing nothing.
 */
export function SignInScreen({ inPlace = false }: { inPlace?: boolean } = {}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  //: **A workspace switch that left a half-session opens on the step the server is waiting for**
  //: (G-P2-02). Read once, when the form mounts; it is a hint and the server still decides.
  const [switching, setSwitching] = useState(switchPendingRead);
  /** Drop the hint: the person left the step, or began a sign-in of their own. */
  function forgetSwitch() {
    clearSwitchPending();
    setSwitching(null);
  }
  const [stage, setStage] = useState<Stage>(
    switching ? (switching.enrolmentRequired ? "enrol" : "code") : "credentials",
  );
  //: The three steps are three headings, so they are three titles (F-008): a tab that said
  //: "Sign in" while the form asked for a second factor would name the step the reader left.
  useDocumentTitle(
    stage === "credentials"
      ? "Sign in"
      : stage === "enrol"
        ? "Set up your second factor"
        : "Enter your code",
  );
  const [message, setMessage] = useState<string | null>(null);

  // Focus follows the step. Pressing "Sign in" or "I have saved them" removes the button that had
  // focus, and a keyboard or screen-reader user is otherwise left at the top of the document. Compared
  // with the previous stage rather than guarded by a "first run" flag, which StrictMode's
  // double-invoked effects defeat (`Join.tsx` does the same).
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef<Stage>(stage);
  useEffect(() => {
    if (shown.current !== stage) {
      shown.current = stage;
      heading.current?.focus();
    }
  }, [stage]);

  /** **Whether the sign-in request itself failed to complete**, which is not the same as a refused
   * credential (EXP-29's "return after interruption"). `mutateAsync` rejects on a dropped
   * connection, the rejection was swallowed by `void submitCredentials(event)`, and the form went
   * quietly back to "Sign in" with nothing said — so the one screen somebody lands on after an
   * interruption was the one that could fail in silence.
   *
   * And the remedy is not "try again": a login that timed out on the way back has usually **set
   * the cookie**, so the honest next step is to reload and let `useMe` answer. */
  const [unreachable, setUnreachable] = useState(false);

  /** **Whether this form is here because the session ended** rather than because nobody had one.
   *
   * Read once, when the form mounts, and *cleared from storage in an effect* — so the sentence is
   * said once, then gone. Reading and clearing in the initialiser would be the neater thing and is
   * wrong: React runs initialisers twice in development and keeps one result, so the flag could be
   * consumed by the run whose answer is thrown away. The effect is idempotent. */
  const [sessionEnded] = useState(sessionEndedPending);
  // Whether a form that was open held typing the session took with it (said in a second sentence).
  const [lostTyping] = useState(sessionEndedLostTyping);
  useEffect(() => {
    if (sessionEnded) clearSessionEnded();
  }, [sessionEnded]);

  const login = useLogin();
  const passkey = usePasskeySignIn();
  //: Asked once. Offered only where the browser can attempt one at all: a button that predictably
  //: fails is design rule 6's defect, and the password form is the whole screen without it.
  const [canUsePasskey] = useState(passkeysSupported);
  const enrol = useEnrolMfa();
  const completeMfa = useCompleteMfa();

  /** Back to the start — `cancel`, on the screen where being stuck matters most.
   *
   * Somebody who typed the wrong address and reached the code stage had no way back but a page
   * reload. It clears the password and the code and never the email, because re-typing an address
   * to correct a password is the thing that makes people give up.
   */
  function startAgain() {
    forgetSwitch();
    setStage("credentials");
    setPassword("");
    setCode("");
    setMessage(null);
    setUnreachable(false);
  }

  // One place decides where a signed-in person goes, and it watches the *server's* answer rather
  // than the mutation's. So completing MFA, completing the password step for a role that needs no
  // second factor, and arriving with a session already in the cookie all land in the same place —
  // three paths that would otherwise each need their own redirect.
  const { data: me } = useMe();
  const navigate = useNavigate();
  useEffect(() => {
    if (!me) return;
    clearSwitchPending();
    // **An ended session returns the reader to where they were, which is where they still are.**
    // `RequireSession` renders this form in place, at the address they were working on, so the
    // right move after signing in is to do nothing: the gate lets the screen through. Sending them
    // to /evidence would be the notice's promise ("carry on where you were") broken one second
    // after it was read. The `/sign-in` route itself is the exception — staying there would leave a
    // signed-in person looking at the form — as is every first visit, which has nowhere to return to.
    if (sessionEnded && inPlace) return;
    // **Overview, not Evidence** (ADR-0151). Sign-in used to land on the evidence queue, so a
    // brand-new Admin met the first-run checklist only after opening Overview by hand: the one
    // screen that says what to do first was one click away from where the product put them.
    // **An Auditor lands in their own place** (ADR-0212 decision 9): Approvals & audit, on the first
    // tab their role is offered, which is the read-only Engagement overview. Home is for the roles
    // that run the inventory; an Auditor's work is the evidence behind a filing.
    if (me.role === "auditor") {
      const entry = entryFor(sectionById("audit"), me.role);
      void navigate({ to: entry.to as "/", search: { method: "location" } });
      return;
    }
    void navigate({ to: "/", search: { method: "location" } });
  }, [me, navigate, sessionEnded, inPlace]);

  async function submitCredentials(event: React.FormEvent) {
    event.preventDefault();
    setMessage(null);
    setUnreachable(false);
    forgetSwitch();
    // No organisation is sent. A form cannot offer a list of workspaces before anyone is
    // authenticated without publishing every tenant's name, so the password resolves it and the
    // switcher in the shell moves between them afterwards.
    let result;
    try {
      result = await login.mutateAsync({ email, password });
    } catch {
      setUnreachable(true);
      return;
    }
    if (result.status === "refused") {
      setMessage(REFUSED);
      return;
    }
    if (result.status === "throttled") {
      setMessage(THROTTLED);
      return;
    }
    if (result.status === "mfa_required") {
      setStage(result.enrolmentRequired ? "enrol" : "code");
      return;
    }
    // Signed in. `useMe` invalidation drives the redirect, so there is nothing to navigate here —
    // one place decides what a signed-in person sees.
  }

  async function submitPasskey() {
    setMessage(null);
    setUnreachable(false);
    forgetSwitch();
    let result;
    try {
      result = await passkey.mutateAsync();
    } catch {
      setUnreachable(true);
      return;
    }
    switch (result.status) {
      case "signed_in":
        // `useMe` invalidation drives the redirect, as for a password.
        return;
      case "mfa_required":
        // The authenticator did not verify the person, so the session is a half-session and the
        // authenticator-app code finishes it. Never an enrolment: the server refuses a passkey
        // sign-in outright for someone with no second factor.
        setStage("code");
        return;
      case "cancelled":
        setMessage(PASSKEY_CANCELLED);
        return;
      case "unsupported":
      case "unavailable":
        setMessage(PASSKEY_UNAVAILABLE);
        return;
      case "refused":
        setMessage(PASSKEY_REFUSED);
        return;
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setMessage(null);
    setUnreachable(false);
    let ok: boolean | "throttled";
    try {
      ok = await completeMfa.mutateAsync(code);
    } catch {
      setUnreachable(true);
      return;
    }
    if (ok === "throttled") setMessage(THROTTLED);
    else if (!ok) setMessage("That code was not accepted. Codes last 30 seconds.");
  }

  /** What the page is saying, if anything, and the one place it is said: above the form, as an icon
   * and words, in a live region. A refused credential is one message for every cause (see the head
   * of this file); a request that did not complete is another, with a different remedy. */
  const notice =
    message || unreachable || sessionEnded ? (
      <>
        {/* Said once, plainly, and only when a session really ended: not on a first visit and not
            after choosing to sign out. */}
        {sessionEnded ? (
          <AuthNotice testId="session-ended-notice">
            {SESSION_ENDED_NOTICE}
            {lostTyping ? ` ${SESSION_ENDED_TYPING_NOTICE}` : ""}
          </AuthNotice>
        ) : null}
        {message ? (
          <AuthNotice id={NOTICE_ID}>{message}</AuthNotice>
        ) : null}
        {/* **The request did not complete, which is not the same as being refused.** Said with the
            remedy that is actually right: a sign-in that timed out on the way back has usually set
            the session cookie already, so reloading lets the server answer rather than asking
            somebody to type their password a second time into a form that may no longer be
            needed. */}
        {unreachable ? (
          <AuthNotice testId="sign-in-unreachable">
            <strong>EmitIQ could not be reached.</strong> That is not a refused password — the
            request did not complete, and a sign-in that times out on the way back has often
            already worked.{" "}
            <button type="button" className={styles.link} onClick={() => window.location.reload()}>
              Reload and check
            </button>
          </AuthNotice>
        ) : null}
      </>
    ) : null;

  /** Wired to the fields a refusal is about, so a screen reader that lands on one hears why. A
   * refused password is about both; a refused code is about the code. A passkey message is about
   * neither field, so it marks nothing invalid. */
  const credentialsInvalid = message === REFUSED;

  return (
    <AuthPage>
      {stage === "credentials" ? (
        <AuthCard title="Sign in" stage="credentials" headingRef={heading} notice={notice}>
          <form className={styles.form} onSubmit={(event) => void submitCredentials(event)}>
            <AuthField label="Email">
              <input
                name="email"
                type="email"
                inputMode="email"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={credentialsInvalid || undefined}
                aria-describedby={credentialsInvalid ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <AuthField label="Password">
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={credentialsInvalid || undefined}
                aria-describedby={credentialsInvalid ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <button className={styles.primary} type="submit" disabled={login.isPending}>
              {login.isPending ? "Checking…" : "Sign in"}
            </button>
          </form>
          {/* **Beside the password form, never instead of it** (ADR-0105). Needs no address typed:
              the authenticator offers the passkeys it holds for this site. Hidden where the
              browser cannot attempt one. */}
          {canUsePasskey ? (
            <>
              <p className={styles.divider} aria-hidden="true">
                or
              </p>
              <button
                className={styles.secondary}
                type="button"
                data-testid="sign-in-passkey"
                disabled={passkey.isPending}
                onClick={() => void submitPasskey()}
              >
                {passkey.isPending ? "Waiting for your passkey…" : "Use a passkey"}
              </button>
            </>
          ) : null}
          <div className={styles.aside}>
            {/* The way in for somebody who holds an invitation token and has never signed in
                (ADR-0046). On the credentials step only: a person partway through a second factor
                is already a member. A plain anchor rather than the router's `Link`, which throws
                when there is no router above it — and this screen is also rendered by
                `RequireSession` in places a test mounts without one. A full page load to `/join`
                is the right cost for a path taken once, by someone with no session to lose. */}
            <p data-testid="sign-in-have-invitation">
              Have an invitation? <a href="/join">Join a workspace</a>
            </p>
            {/* A plain anchor for the same reason as the line above. Offered on the credentials
                step only, and never says whether an address has an account. */}
            <p data-testid="sign-in-forgot">
              <a href="/reset-password">Forgot your password?</a>
            </p>
          </div>
        </AuthCard>
      ) : null}

      {stage === "enrol" ? (
        <AuthCard
          title="Set up your second factor"
          sub={
            switching?.workspace ? `Required to open ${switching.workspace}` : "Required to sign in"
          }
          stage="enrol"
          headingRef={heading}
          notice={notice}
        >
          <p className={styles.prose}>
            Your role or this workspace's policy requires MFA before you can sign in. Admins and
            Approvers always require it.
          </p>
          {enrol.data ? (
            <>
              <p className={styles.prose}>
                Scan this code with your authenticator app, or type the key beneath it in by hand,
                then enter a code from the app.
              </p>
              {/* **An addition to the typed key, never a replacement** (ADR-0059, condition 3):
                  an authenticator that cannot scan, a password manager's TOTP field and a refused
                  camera all need the characters, so the key below is not behind a disclosure and
                  does not depend on this rendering. The URI is a credential — it is passed to the
                  component and nowhere else, and the accessible name is written here so that it
                  cannot be derived from it. */}
              <QrCode
                value={enrol.data.provisioning_uri}
                label="QR code for your authenticator app"
                testId="enrol-qr"
              />
              <code className={styles.secret} data-testid="enrol-secret">
                {enrol.data.secret}
              </code>
              <p className={styles.prose}>
                <strong>Save these recovery codes now.</strong> They are shown once, each works
                once, and they are the only way back in if you lose your phone.
              </p>
              <ul className={styles.codes}>
                {enrol.data.recovery_codes.map((recovery) => (
                  <li key={recovery}>
                    <code>{recovery}</code>
                  </li>
                ))}
              </ul>
              <button className={styles.primary} type="button" onClick={() => setStage("code")}>
                I have saved them
              </button>
            </>
          ) : (
            <>
              {/* **Two refusals, two remedies, and the old copy gave the impossible one to
                  both.** A `PENDING_MFA` session lives five minutes, which is less time than
                  installing an authenticator app takes the first time, so the expired case is
                  the *likely* one here — and "try again, or reload this page" is the one thing
                  that cannot work once the session is gone. */}
              {enrol.isError ? (
                isSignedOut(enrol.error) ? (
                  <AuthNotice testId="enrol-session-expired">
                    <strong>Your sign-in timed out before you finished.</strong> Nothing has been
                    changed on your account and no second factor was set up. Sign in again, then
                    set up your authenticator straight away — there are five minutes to do it in.
                  </AuthNotice>
                ) : (
                  <AuthNotice>
                    The setup key could not be prepared. Nothing has been changed on your account —
                    try again, or reload this page.
                  </AuthNotice>
                )
              ) : null}
              <button
                className={styles.primary}
                type="button"
                onClick={() => enrol.mutate()}
                disabled={enrol.isPending}
              >
                {enrol.isPending ? "Preparing…" : "Show my setup key"}
              </button>
              {/* Offered **only before the codes are shown**. They are shown once, and a back
                  control beside them would be a way to lose them by one click. */}
              <button
                className={styles.link}
                type="button"
                data-testid="sign-in-start-again"
                onClick={startAgain}
              >
                Use a different account
              </button>
            </>
          )}
        </AuthCard>
      ) : null}

      {stage === "code" ? (
        <AuthCard
          title="Enter your code"
          sub={
            switching?.workspace
              ? `To open ${switching.workspace}. From your authenticator, or a recovery code`
              : "From your authenticator, or a recovery code"
          }
          stage="code"
          headingRef={heading}
          notice={notice}
        >
          <form className={styles.form} onSubmit={(event) => void submitCode(event)}>
            <AuthField label="Code">
              <input
                name="code"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="done"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                aria-invalid={message ? true : undefined}
                aria-describedby={message ? NOTICE_ID : undefined}
                required
              />
            </AuthField>
            <button className={styles.primary} type="submit" disabled={completeMfa.isPending}>
              {completeMfa.isPending ? "Checking…" : "Continue"}
            </button>
            <button
              className={styles.link}
              type="button"
              data-testid="sign-in-start-again"
              onClick={startAgain}
            >
              Use a different account
            </button>
          </form>
        </AuthCard>
      ) : null}
    </AuthPage>
  );
}
